import { ServiceModeLocationProps, ServiceModeLocationType, useMqSelect } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { ServiceMode, useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useUIContext } from '@fhs-legacy/frontend/src/state/ui';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

function getTypeFromServiceMode(opts: {
  serviceMode?: ServiceMode;
  enableDeliveryFeeOnServiceModeBar?: boolean;
}): ServiceModeLocationType {
  if (!opts.serviceMode) {
    return 'location';
  }

  if (
    opts.serviceMode === ServiceMode.CATERING_DELIVERY ||
    opts.serviceMode === ServiceMode.CATERING_PICKUP
  ) {
    return 'catering';
  }

  if (opts.serviceMode === ServiceMode.DELIVERY) {
    return opts.enableDeliveryFeeOnServiceModeBar ? 'deliveryWithFees' : 'delivery';
  }

  return 'pickup';
}

type RetValue = Pick<
  ServiceModeLocationProps,
  'loading' | 'type' | 'storeText' | 'serviceModeSubtitle'
>;

export function useServiceModeLocation(): RetValue {
  const { serviceMode, deliveryFees } = useServiceModeContext();
  const enableDeliveryFeeOnServiceModeBar = useFlag('show-delivery-fee-on-service-mode-bar');

  const isDelivery = serviceMode === ServiceMode.DELIVERY;

  const { store } = useStoreContext();
  const { deliveryAddress } = useOrderContext();

  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();

  const type = getTypeFromServiceMode({ serviceMode, enableDeliveryFeeOnServiceModeBar });

  const nonDelivery = {
    loading: false,
    type,
    serviceModeSubtitle: '',
    storeText: useMqSelect(
      {
        $gteDesktop: store.name,
      },
      [store.name, store.physicalAddress?.address1].filter(Boolean).join(' • ')
    ),
  };

  if (!isDelivery) {
    return nonDelivery;
  }

  return {
    loading: false,
    type,
    storeText: deliveryAddress?.addressLine1,
    serviceModeSubtitle:
      enableDeliveryFeeOnServiceModeBar && deliveryFees?.deliveryTotalFee
        ? `${currencyFormatter(deliveryFees.deliveryTotalFee)} Delivery Fee (Per Order)`
        : '',
  };
}
